import {
  ActionIcon,
  Box,
  Button,
  Card,
  Divider,
  Flex,
  Group,
  Stack,
  Text,
  Title,
  useMantineColorScheme,
} from "@mantine/core";
import { IconPencil } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { TextSerif } from "@/components/elements";
import { formatDate } from "@/utils/formatDate";
import { formatCurrency } from "@/utils/formatEuro";

import type { Dispatch, FC, SetStateAction } from "react";
import type { CarForm } from "../interfaces/CreateCarForm.types";

interface Props {
  form: CarForm;
  loading: boolean;
  onCancel?: () => void;
  onSubmit: () => void;
  activeStep: number;
  setActiveStep: Dispatch<SetStateAction<number>>;
}

const Step4: FC<Props> = ({ form, loading, onCancel, onSubmit, activeStep, setActiveStep }) => {
  const { t } = useTranslation();

  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  const cardBG = isDark ? "#222222" : "#f7f8fa";
  const labelColor = isDark ? "#a1a1aa" : "#6b7280";

  const renderRow = (label: string, value?: string | number | null) => (
    <Flex justify="space-between" align="center">
      <TextSerif fz="sm" c={labelColor}>
        {label}
      </TextSerif>

      <Text fz="sm" fw={500}>
        {value !== undefined && value !== null && value !== "" ? String(value) : "-"}
      </Text>
    </Flex>
  );

  return (
    <Stack gap="lg">
      <Title order={4}>{t("components.CreateCarForm.titles.reviewAndConfirm")}</Title>

      <Card bg={cardBG} withBorder radius="sm" p="lg">
        <Flex justify="space-between" align="center" mb="md">
          <Title order={5}>{t("components.CreateCarForm.basics")}</Title>

          <ActionIcon variant="light" onClick={() => setActiveStep(0)} aria-label="Edit basics">
            <IconPencil size={18} />
          </ActionIcon>
        </Flex>

        <Stack gap="md">
          {renderRow(t("car.carNickname"), form.values.nickname)}
          {renderRow(t("car.owner"), form.values.owner)}
          {renderRow(
            t("car.purchase_price"),
            form.values.purchase_price
              ? formatCurrency(Number(form.values.purchase_price), "EUR")
              : "-"
          )}
          {renderRow(
            t("car.purchase_date"),
            form.values.purchase_date ? formatDate(String(form.values.purchase_date)) : "-"
          )}
          {renderRow(t("car.make"), form.values.make)}
          {renderRow(
            t("car.status"),
            form.values.status ? t(`car.statuses.${form.values.status}`) : "-"
          )}
        </Stack>
      </Card>

      <Card bg={cardBG} withBorder radius="sm" p="lg">
        <Flex justify="space-between" align="center" mb="md">
          <Title order={5}>{t("components.CreateCarForm.carDetails")}</Title>

          <ActionIcon
            variant="light"
            onClick={() => setActiveStep(1)}
            aria-label="Edit car details"
          >
            <IconPencil size={18} />
          </ActionIcon>
        </Flex>

        <Stack gap="md">
          {renderRow(t("car.family"), form.values.family)}
          {renderRow(t("car.generation"), form.values.generation)}
          {renderRow(t("car.model"), form.values.model)}
          {renderRow(
            t("car.variant"),
            form.values.variant === "no_variant" ? t("car.no_variant") : form.values.variant
          )}
          {renderRow(t("car.production_year"), form.values.production_year)}
        </Stack>
      </Card>

      <Card bg={cardBG} withBorder radius="sm" p="lg">
        <Flex justify="space-between" align="center" mb="md">
          <Title order={5}>{t("components.CreateCarForm.extraDetails")}</Title>

          <ActionIcon
            variant="light"
            onClick={() => setActiveStep(2)}
            aria-label="Edit extra details"
          >
            <IconPencil size={18} />
          </ActionIcon>
        </Flex>

        <Stack gap="md">
          {renderRow(t("car.licence_plate"), form.values.licence_plate)}
          {renderRow(t("car.country_of_registration"), form.values.country_of_registration)}
          {renderRow(t("car.vin"), form.values.vin)}
          {renderRow(t("car.color"), form.values.color)}
          {renderRow(t("car.interior_colors"), form.values.interior_colors)}
          {renderRow(
            t("car.condition"),
            form.values.condition ? t(`car.conditions.${form.values.condition}`) : "-"
          )}
          {renderRow(t("car.number_of_previous_owner"), form.values.number_of_previous_owner)}
          {renderRow(t("car.special_equipment"), form.values.special_equipment)}
        </Stack>
      </Card>

      <Divider />

      <Group justify="space-between" mt="2em">
        <Text c="var(--mantine-color-primaryDark-5)" fw={500} fz="sm">
          {t("common.steps", {
            currentStep: activeStep + 1,
            totalSteps: 4,
          })}
        </Text>
        <Flex justify="end" gap="md">
          <Group justify="space-" mt="sm">
            {onCancel ? (
              <Button variant="outline" onClick={onCancel} disabled={loading} c="dark">
                {t("common.cancel")}
              </Button>
            ) : (
              <Box />
            )}

            <Button type="button" onClick={onSubmit} loading={loading}>
              {t("common.create")}
            </Button>
          </Group>
        </Flex>
      </Group>
    </Stack>
  );
};

export default Step4;
